import React from 'react';
import { Menu, MenuItem } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import useListDevicesSocket from '../../stores/ListDevicesSocketState';
import useSystemState from '~/features/System/stores/SystemState';
import useDispatchState from '~/features/Dispatch/stores/DispatchState';
import { associateDeviceInDispatch } from '../../services';
import useDispatchVehicleState from '~/features/Dispatch/stores/DispatchVehicleState';
import { useHookstate } from '@hookstate/core';
import ConfirmDialog from '~/components/ConfirmDialog';
import notify from '~/utils/notify';

interface MenuListProps {
  readonly id: string
  readonly anchorEl: HTMLElement | null
  readonly handleClose: () => void
  readonly readOnly?: boolean;
  readonly typeStatus?: string;
}

export default function MenuList({ id, anchorEl, handleClose, readOnly = false, typeStatus }: MenuListProps) {
  const { translate } = useLang();
  const { listDevicesState } = useListDevicesSocket();
  const { systemConfig } = useSystemState()
  const { dispatch } = useDispatchState()
  const { syncVehicleListInDispatch } = useDispatchVehicleState();
  const {
    openConectDispatchDevice,
    openOutOfServiceDialog,
    openReturnToServiceDialog,
    canConnectDevice,
    canDisconnectDevice,
    canSeeCrew,
    canSetOutOfService,
    canSetReturnToService,
    canSetOnMission,
    canSetBackToAvailable,
    openCrewDialog,
    openDisconnectDialog,
    canTransferDevice,
    openTransferDevice,
    openChronologyDevice,
    openBackToAvailable,
    openOnMission,
    deviceId,
    identification,
    dispatchGroup,
  } = listDevicesState();
  const openConfirmDialog = useHookstate(false);

  const verifySameDispatchGroup = () => {
    const deviceDispatchGroup = dispatchGroup.get();
    const dispatchDispatchGroup = dispatch().dispatchGroup.id.get();
    return deviceDispatchGroup === dispatchDispatchGroup;
  }

  return (
    <>
      <Menu
        id={id}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {canConnectDevice.get() && !readOnly &&
          <MenuItem onClick={() => {
            openConectDispatchDevice.set(true);
            handleClose();
          }}>
            {translate('Conectar')}
          </MenuItem>
        }
        {(canConnectDevice.get() || canDisconnectDevice.get()) && !readOnly &&
          <MenuItem onClick={() => {
            openTransferDevice.set(true);
            handleClose();
          }}>
            {translate('Transfer')}
          </MenuItem>
        }
        {canSeeCrew.get() && <MenuItem onClick={() => {
          openCrewDialog.set(!openCrewDialog.get())
          handleClose()
        }}>
          {translate('See Crew')}
        </MenuItem>}
        {canSetOutOfService.get() && !readOnly && <MenuItem onClick={() => {
          openOutOfServiceDialog.set(true)
          handleClose()
        }}>
          {translate('Out of service')}
        </MenuItem>}
        {Boolean(Number(systemConfig().enableOnMissionStatusOfDevice.get())) &&
          canSetOnMission.get() && !readOnly && <MenuItem onClick={() => {
            openOnMission.set(true)
            handleClose()
          }}>
            {translate('On Mission')}
          </MenuItem>}
        {canSetBackToAvailable.get() && !readOnly && <MenuItem onClick={() => {
          openBackToAvailable.set(true)
          handleClose()
        }}>
          {translate('Back to available')}
        </MenuItem>}
        <MenuItem onClick={() => {
          openChronologyDevice.set(true)
          handleClose()
        }}>
          {translate('Chronology device')}
        </MenuItem>
        {!canConnectDevice.get() && canDisconnectDevice.get() && !readOnly && <MenuItem onClick={() => {
          openDisconnectDialog.set(true);
          handleClose()
        }}>
          {translate('Disconnect')}
        </MenuItem>}
        {(canSetReturnToService.get() && !readOnly) && <MenuItem onClick={() => {
          openReturnToServiceDialog.set(true)
          handleClose()
        }} >
          {translate('Return to service')}
        </MenuItem>}
        {typeStatus === 'available' && <MenuItem onClick={() => {
          verifySameDispatchGroup()
            ? openConfirmDialog.set(true)
            : notify({
              message: translate('Device and dispatch must be from the same dispatch group'),
              type: 'error',
            })
          handleClose()
        }}>
          {translate('Associate Device')}
        </MenuItem>}
      </Menu>
      <ConfirmDialog
        open={openConfirmDialog}
        title={translate('Confirm association')}
        content={`${translate('Associate device to dispatch',
          { deviceId: identification.get(), codeDispatch: dispatch().code.get() })}`
        }
        handleClose={() => openConfirmDialog.set(false)}
        handleAgree={() => {
          associateDeviceInDispatch({ deviceId: deviceId.get(), dispatchId: dispatch().id.get() })
            .finally(() => {
              syncVehicleListInDispatch(dispatch().id.get()).finally(() => {
                openConfirmDialog.set(false);
              });
              notify({
                message: translate('Added successfully'),
                type: 'success',
              });
            });
        }}
        closeLabel="Cancel"
        AgreeLabel="Confirm"
      />
    </>
  )
}